import React, { useState } from 'react';
import { Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import ExcelJS from 'exceljs';
import { getRegistroNombre } from './file-utils';

const REGISTRO_LABELS = {
  participante: 'Participante',
  organizacion: 'Organización',
  sin_registro: 'Sin registro',
};

const ExportarGestionArchivos = ({
  filteredFiles,
  stats,
  selectedProject,
  selectedMonth,
  registroFilter,
  projects,
  months
}) => {
  const [exporting, setExporting] = useState(false);

  const cleanFileName = (name) => {
    return name.replace(/^[0-9]+-/, "");
  };

  const getIdentificador = (file) => {
    if (file.tipoRegistro === 'organizacion' && file.organizacion) {
      return file.organizacion.Nit || file.organizacion.sigla || '';
    }
    return file.cedula || '';
  };

  const getFilterLabels = () => {
    const filters = [];

    if (selectedProject !== "all") {
      filters.push(`Proyecto: ${selectedProject}`);
    }

    if (selectedMonth !== "all") {
      filters.push(`Mes: ${selectedMonth}`);
    }

    if (registroFilter !== "all") {
      filters.push(REGISTRO_LABELS[registroFilter] || registroFilter);
    }

    return filters.join(" | ");
  };

  const exportarAExcel = async () => {
    setExporting(true);

    try {
      const workbook = new ExcelJS.Workbook();

      // Hoja 1: Resumen y Lista de Archivos
      const worksheet = workbook.addWorksheet('Gestión de Archivos');

      // Hoja 2: Estadísticas Detalladas
      const worksheetStats = workbook.addWorksheet('Estadísticas');

      // Estilos
      const estiloTitulo = {
        font: { bold: true, size: 16, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '042956' } },
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      const estiloSubtitulo = {
        font: { bold: true, size: 12 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E3F2FD' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const estiloCabecera = {
        font: { bold: true, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '1976D2' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const estiloCelda = {
        alignment: { vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const estiloCeldaCentrada = {
        ...estiloCelda,
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      const estiloCeldaIzquierda = {
        ...estiloCelda,
        alignment: { horizontal: 'left', vertical: 'middle' }
      };

      // --- HOJA 1: GESTIÓN DE ARCHIVOS ---
      // Título
      worksheet.mergeCells('A1:G1');
      const titulo = worksheet.getCell('A1');
      titulo.value = 'GESTIÓN DE ARCHIVOS - REPORTE';
      titulo.style = estiloTitulo;

      // Fecha y filtros
      const fecha = new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      worksheet.mergeCells('A2:G2');
      worksheet.getCell('A2').value = `Generado el: ${fecha}`;
      worksheet.getCell('A2').style = {
        font: { italic: true, color: { argb: '7F8C8D' } },
        alignment: { horizontal: 'left' }
      };

      // Filtros aplicados
      const filtrosTexto = getFilterLabels();
      if (filtrosTexto) {
        worksheet.mergeCells('A3:G3');
        worksheet.getCell('A3').value = `Filtros aplicados: ${filtrosTexto}`;
        worksheet.getCell('A3').style = {
          font: { bold: true, color: { argb: 'D84315' } },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFECB3' } }
        };
      }

      worksheet.addRow([]);

      // Estadísticas rápidas
      worksheet.mergeCells('A5:G5');
      worksheet.getCell('A5').value = 'RESUMEN DE ARCHIVOS';
      worksheet.getCell('A5').style = estiloSubtitulo;

      // Crear tabla de estadísticas con fondo blanco
      const statsData = [
        ['Total de archivos', stats.totalFiles],
        ['Con participante', stats.filesWithParticipante],
        ['Con organización', stats.filesWithOrganizacion],
        ['Sin registro', stats.filesSinRegistro],
        ['Porcentaje sin registro',
          stats.totalFiles > 0
            ? `${Math.round((stats.filesSinRegistro / stats.totalFiles) * 100)}%`
            : '0%'
        ]
      ];

      // Agregar filas con fondo blanco
      statsData.forEach(([label, value], index) => {
        const fila = 6 + index;

        // Celda de etiqueta
        worksheet.getCell(`A${fila}`).value = label;
        worksheet.getCell(`A${fila}`).style = {
          ...estiloCeldaIzquierda,
          font: { bold: true },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }
        };

        // Celda de valor (cambiamos de columna para que esté al lado)
        worksheet.getCell(`B${fila}`).value = value;
        worksheet.getCell(`B${fila}`).style = {
          ...estiloCeldaCentrada,
          font: { bold: true },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }
        };

        // Mezclar celdas C-G para mantener la tabla de 2 columnas
        if (fila <= 10) {
          worksheet.mergeCells(`C${fila}:G${fila}`);
        }
      });

      worksheet.addRow([]);
      worksheet.addRow([]);

      // Cabecera de la tabla
      const cabecera = ['#', 'Archivo', 'Proyecto', 'Mes', 'Tipo', 'Registrado a', 'Identificador'];
      const filaCabeceraRow = 13; // Después del resumen y espacios
      const filaCabecera = worksheet.addRow(cabecera);

      // Mover la fila a la posición correcta
      worksheet.spliceRows(filaCabeceraRow, 0, filaCabecera.values);
      worksheet.spliceRows(filaCabeceraRow + 1, 1); // Eliminar la fila duplicada

      // Aplicar estilo a la cabecera
      const filaCabeceraActual = worksheet.getRow(filaCabeceraRow);
      filaCabeceraActual.eachCell((cell) => {
        cell.style = estiloCabecera;
      });

      // Datos de archivos
      filteredFiles.forEach((file, index) => {
        const nombreLimpio = cleanFileName(file.name);
        const tipoLabel = REGISTRO_LABELS[file.tipoRegistro] || 'Sin registro';
        const registradoA = getRegistroNombre(file) || 'SIN ASIGNAR';
        const identificador = getIdentificador(file) || 'N/A';

        const fila = [
          index + 1,
          nombreLimpio,
          file.project || 'No especificado',
          file.month || 'No especificado',
          tipoLabel,
          registradoA,
          identificador
        ];

        const filaDatos = worksheet.addRow(fila);
        filaDatos.eachCell((cell, colNumber) => {
          cell.style = {
            ...estiloCeldaCentrada,
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }
          };

          // Resaltar tipo de registro
          if (colNumber === 5) {
            if (file.tipoRegistro === 'participante') {
              cell.font = { color: { argb: '2E7D32' }, bold: true };
            } else if (file.tipoRegistro === 'organizacion') {
              cell.font = { color: { argb: '1976D2' }, bold: true };
            } else {
              cell.font = { color: { argb: 'C62828' }, bold: true };
            }
          }

          // Solo resaltar celdas específicas con problemas
          if (colNumber === 3 && (!file.project || file.project === 'undefined')) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3E0' } };
          }

          if (colNumber === 4 && (!file.month || file.month === 'undefined')) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3E0' } };
          }
        });
      });

      // Totales
      const lastRow = worksheet.rowCount + 1;
      const filaTotal = worksheet.addRow([
        'TOTALES',
        '',
        '',
        '',
        '',
        '',
        `Archivos: ${filteredFiles.length} | Participantes: ${stats.filesWithParticipante} | Organizaciones: ${stats.filesWithOrganizacion} | Sin registro: ${stats.filesSinRegistro}`
      ]);

      worksheet.mergeCells(`A${lastRow}:F${lastRow}`);

      filaTotal.eachCell((cell) => {
        cell.style = {
          ...estiloCeldaCentrada,
          font: { bold: true },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F5F5F5' } } // Gris claro, no verde
        };
      });

      // Ajustar anchos
      worksheet.columns = [
        { width: 8 },   // #
        { width: 40 },  // Archivo
        { width: 25 },  // Proyecto
        { width: 20 },  // Mes
        { width: 16 },  // Tipo
        { width: 32 },  // Registrado a
        { width: 20 }   // Identificador
      ];

      // --- HOJA 2: ESTADÍSTICAS DETALLADAS ---
      worksheetStats.mergeCells('A1:F1');
      worksheetStats.getCell('A1').value = 'ESTADÍSTICAS DETALLADAS';
      worksheetStats.getCell('A1').style = estiloTitulo;

      // Fecha en hoja 2 también
      worksheetStats.mergeCells('A2:F2');
      worksheetStats.getCell('A2').value = `Generado el: ${fecha}`;
      worksheetStats.getCell('A2').style = {
        font: { italic: true, color: { argb: '7F8C8D' } },
        alignment: { horizontal: 'left' }
      };

      let filaActual = 4;

      // Distribución por proyecto
      if (filteredFiles.length > 0) {
        worksheetStats.mergeCells(`A${filaActual}:F${filaActual}`);
        worksheetStats.getCell(`A${filaActual}`).value = 'DISTRIBUCIÓN POR PROYECTO';
        worksheetStats.getCell(`A${filaActual}`).style = estiloSubtitulo;
        filaActual++;

        const distribucionProyecto = filteredFiles.reduce((acc, file) => {
          const proyecto = file.project || 'No especificado';
          if (!acc[proyecto]) {
            acc[proyecto] = { total: 0, conParticipante: 0, conOrganizacion: 0, sinRegistro: 0 };
          }
          acc[proyecto].total++;
          if (file.tipoRegistro === 'participante') {
            acc[proyecto].conParticipante++;
          } else if (file.tipoRegistro === 'organizacion') {
            acc[proyecto].conOrganizacion++;
          } else {
            acc[proyecto].sinRegistro++;
          }
          return acc;
        }, {});

        // Cabecera proyectos
        const cabeceraProyectos = ['Proyecto', 'Total Archivos', 'Participantes', 'Organizaciones', 'Sin Registro', '% Con Registro'];
        const filaCabeceraProyectos = worksheetStats.addRow(cabeceraProyectos);
        filaCabeceraProyectos.eachCell((cell) => {
          cell.style = estiloCabecera;
        });
        filaActual++;

        Object.entries(distribucionProyecto).forEach(([proyecto, datos]) => {
          const porcentaje = datos.total > 0 ? Math.round(((datos.conParticipante + datos.conOrganizacion) / datos.total) * 100) : 0;

          const fila = [
            proyecto,
            datos.total,
            datos.conParticipante,
            datos.conOrganizacion,
            datos.sinRegistro,
            `${porcentaje}%`
          ];

          const filaProyecto = worksheetStats.addRow(fila);
          filaProyecto.eachCell((cell, colNumber) => {
            cell.style = {
              ...estiloCeldaCentrada,
              fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }
            };

            // Solo texto de color, no fondo
            if (colNumber === 6) {
              if (porcentaje >= 80) {
                cell.font = { color: { argb: '2E7D32' }, bold: true };
              } else if (porcentaje >= 50) {
                cell.font = { color: { argb: 'FF9800' }, bold: true };
              } else {
                cell.font = { color: { argb: 'D32F2F' }, bold: true };
              }
            }
          });

          filaActual++;
        });

        worksheetStats.addRow([]);
        filaActual++;

        // Distribución por mes
        if (selectedProject !== "all" && filteredFiles.some(f => f.month)) {
          worksheetStats.mergeCells(`A${filaActual}:F${filaActual}`);
          worksheetStats.getCell(`A${filaActual}`).value = 'DISTRIBUCIÓN POR MES';
          worksheetStats.getCell(`A${filaActual}`).style = estiloSubtitulo;
          filaActual++;

          const distribucionMes = filteredFiles.reduce((acc, file) => {
            const mes = file.month || 'No especificado';
            if (!acc[mes]) {
              acc[mes] = { total: 0, conParticipante: 0, conOrganizacion: 0, sinRegistro: 0 };
            }
            acc[mes].total++;
            if (file.tipoRegistro === 'participante') {
              acc[mes].conParticipante++;
            } else if (file.tipoRegistro === 'organizacion') {
              acc[mes].conOrganizacion++;
            } else {
              acc[mes].sinRegistro++;
            }
            return acc;
          }, {});

          // Cabecera meses
          const cabeceraMeses = ['Mes', 'Total Archivos', 'Participantes', 'Organizaciones', 'Sin Registro', '% Con Registro'];
          const filaCabeceraMeses = worksheetStats.addRow(cabeceraMeses);
          filaCabeceraMeses.eachCell((cell) => {
            cell.style = estiloCabecera;
          });
          filaActual++;

          Object.entries(distribucionMes).forEach(([mes, datos]) => {
            const porcentaje = datos.total > 0 ? Math.round(((datos.conParticipante + datos.conOrganizacion) / datos.total) * 100) : 0;

            const fila = [
              mes,
              datos.total,
              datos.conParticipante,
              datos.conOrganizacion,
              datos.sinRegistro,
              `${porcentaje}%`
            ];

            const filaMes = worksheetStats.addRow(fila);
            filaMes.eachCell((cell, colNumber) => {
              cell.style = {
                ...estiloCeldaCentrada,
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }
              };

              if (colNumber === 6) {
                if (porcentaje >= 80) {
                  cell.font = { color: { argb: '2E7D32' }, bold: true };
                } else if (porcentaje >= 50) {
                  cell.font = { color: { argb: 'FF9800' }, bold: true };
                } else {
                  cell.font = { color: { argb: 'D32F2F' }, bold: true };
                }
              }
            });

            filaActual++;
          });
        }
      }

      worksheetStats.addRow([]);
      filaActual++;

      // Proyectos disponibles
      worksheetStats.mergeCells(`A${filaActual}:F${filaActual}`);
      worksheetStats.getCell(`A${filaActual}`).value = 'PROYECTOS DISPONIBLES EN EL SISTEMA';
      worksheetStats.getCell(`A${filaActual}`).style = estiloSubtitulo;
      filaActual++;

      const filaProyectosDisponibles = worksheetStats.addRow(['Proyecto', '', '', '', '', '']);
      filaProyectosDisponibles.eachCell((cell, colNumber) => {
        if (colNumber === 1) {
          cell.style = estiloCabecera;
        } else {
          cell.style = {
            ...estiloCabecera,
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '1976D2' } }
          };
        }
      });
      filaActual++;

      projects.forEach((proyecto) => {
        const fila = worksheetStats.addRow([proyecto, '', '', '', '', '']);
        fila.eachCell((cell, colNumber) => {
          if (colNumber === 1) {
            cell.style = {
              ...estiloCeldaIzquierda,
              fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF' } }
            };
          }
        });
        filaActual++;
      });

      // Ajustar anchos estadísticas
      worksheetStats.columns = [
        { width: 30 },
        { width: 15 },
        { width: 15 },
        { width: 15 },
        { width: 15 },
        { width: 15 }
      ];

      // Generar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const nombreArchivo = `Gestion_Archivos_${new Date().toISOString().split('T')[0]}.xlsx`;

      link.href = url;
      link.download = nombreArchivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Limpiar URL
      setTimeout(() => URL.revokeObjectURL(url), 100);

    } catch (error) {
      console.error('Error al exportar:', error);
      alert('Error al exportar el archivo. Por favor, intente de nuevo.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      type="primary"
      icon={<DownloadOutlined />}
      onClick={exportarAExcel}
      loading={exporting}
      disabled={!filteredFiles || filteredFiles.length === 0}
      title="Exportar gestión de archivos a Excel"
      style={{ marginLeft: 'auto' }}
    >
      Exportar Excel
    </Button>
  );
};

export default ExportarGestionArchivos;
